import { useState, useCallback } from 'react';
import { sendPhoneVerificationSmsAPI } from '@common/api/api';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { addParamsToAnalitycs } from '@app/actions/application';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import { withCaptcha } from '@common/config/utils';

/**
 * Hook for phone verification functionality
 * Handles sending SMS codes and verifying phone verification codes
 */

interface UsePhoneVerificationProps {
  clickSource?: 'signup' | 'settings';
  smsAttempts?: number;
  incrementSmsAttempts?: () => number;
}

export const usePhoneVerification = ({
  clickSource = 'signup',
  smsAttempts = 0,
  incrementSmsAttempts
}: UsePhoneVerificationProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [timerEndsAt, setTimerEndsAt] = useState<string | null>(null);

  const sendSmsCode = useCallback(async (phoneNumber: string, isResendAttempt: boolean = false): Promise<string | null> => {
    if (loading) return null;

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Increment SMS attempts count - always use increment function if provided
      // This ensures we get the latest value from state, not from stale closure
      let currentSmsAttempts: number;
      if (incrementSmsAttempts) {
        // Call increment function to update state and get new value
        try {
          currentSmsAttempts = incrementSmsAttempts();
        } catch (err) {
          log(LOG_LEVELS.ERROR, 'PhoneVerification', 'Failed to increment SMS attempts', { err });
          // Fallback to prop value + 1
          currentSmsAttempts = (smsAttempts || 0) + 1;
        }
      } else {
        // Fallback: increment manually if function not provided
        // Use current smsAttempts prop value (may be stale, but better than nothing)
        currentSmsAttempts = (smsAttempts || 0) + 1;
      }

      // Ensure we have a valid number
      if (typeof currentSmsAttempts !== 'number' || Number.isNaN(currentSmsAttempts) || currentSmsAttempts < 1) {
        log(LOG_LEVELS.ERROR, 'PhoneVerification', 'Invalid SMS attempts value, resetting to 1', {
          currentSmsAttempts
        });
        currentSmsAttempts = 1;
      }

      window.store.dispatch(addParamsToAnalitycs({
        [ANALYTICS_EVENTS.SMS_VERIFICATION_REQUESTED]: {
          'click source': clickSource,
          'sms attempts in sms flow': currentSmsAttempts,
          'resend code attempt': isResendAttempt
        }
      }));
      const context = clickSource === 'settings' ? 'connect' : 'auth';

      const response: any = await new Promise((resolve, reject) => {
        withCaptcha('sms', (token: string) => {
          sendPhoneVerificationSmsAPI(phoneNumber, context, token).then(resolve).catch(reject);
        });
      });

      // Extract timer_ends_at from response
      // API response is converted to camelCase, but check both formats
      const timerEndsAtValue = (response as any)?.timerEndsAt;

      if (timerEndsAtValue) {
        setTimerEndsAt(timerEndsAtValue);
      } else {
        setTimerEndsAt(null);
      }

      setSuccess(true);
      log(LOG_LEVELS.INFO, 'PhoneVerification', 'SMS code sent successfully', { timerEndsAt: timerEndsAtValue });

      return timerEndsAtValue;
    } catch (err: any) {
      log(LOG_LEVELS.ERROR, 'PhoneVerification', 'Failed to send SMS code', { err });

      // Parse error response if it's a Response object
      let parsedError = err;
      const errorStatus = err?.status; // Save status before parsing

      if (err && typeof err.json === 'function') {
        try {
          const jsonError = await err.json();
          // Convert to camelCase
          const { toCamelCase } = await import('@common/config/utils');
          const camelCaseError = toCamelCase(jsonError);
          parsedError = {
            ...err,
            status: errorStatus, // Preserve status
            ...camelCaseError,
            errors: camelCaseError.errors || camelCaseError.error || camelCaseError
          };
        } catch (_parseErr) {
          // If parsing fails, use original error
          parsedError = { ...err, status: errorStatus };
        }
      } else {
        // Error might already be parsed
        parsedError = { ...err, status: errorStatus };
      }

      // Extract timer_ends_at from errors (for rate limiting) - 422 status
      if (parsedError.status === 422 && parsedError.errors) {
        let timerEndsAtFromError: string | null = null;

        if (parsedError.errors.timerEndsAt && Array.isArray(parsedError.errors.timerEndsAt)) {
          const [firstTimerEndsAt] = parsedError.errors.timerEndsAt;
          timerEndsAtFromError = firstTimerEndsAt || null;
        }

        if (timerEndsAtFromError) {
          log(LOG_LEVELS.INFO, 'PhoneVerification', 'Setting timerEndsAt from error', { timerEndsAtFromError });
          setTimerEndsAt(timerEndsAtFromError);
        }
      }

      // Extract error message as string
      let errorMessage: string | null = null;

      if (parsedError.errors?.base && Array.isArray(parsedError.errors.base)) {
        const [firstBaseError] = parsedError.errors.base;
        errorMessage = firstBaseError || null;
      } else if (parsedError.fullMessages && Array.isArray(parsedError.fullMessages)) {
        const [firstFullMessage] = parsedError.fullMessages;
        errorMessage = firstFullMessage || null;
      } else if (parsedError.errors?.mobilePhoneId && Array.isArray(parsedError.errors?.mobilePhoneId)) {
        const [firstFullMessage] = parsedError.errors.mobilePhoneId;
        errorMessage = firstFullMessage || null;
      } else if (parsedError?.error && typeof parsedError.error === 'string') {
        errorMessage = parsedError.error || null;
      } else {
        errorMessage = 'Failed to send verification code. Please try again.';
      }

      setError(errorMessage);
      setSuccess(false);
      // Throw parsed error with errors object for component access
      const errorToThrow = new Error('Failed to send SMS code');
      Object.assign(errorToThrow, { ...parsedError, errors: parsedError.errors || parsedError });
      throw errorToThrow;
    } finally {
      setLoading(false);
    }
  }, [loading, clickSource, incrementSmsAttempts]);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
    setLoading(false);
    setTimerEndsAt(null);
  }, []);

  return {
    sendSmsCode,
    loading,
    error,
    success,
    timerEndsAt,
    reset
  };
};
